import * as React from "react";
import { Form } from "semantic-ui-react";
import MaskedInput from "react-text-mask";
import * as moment from "moment";
import { validate } from "not-valid";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { QuestionInjectedProps, QuestionProps } from "../../model";
import { isQuestionDisabled } from "../../questionnaireConfig/models";
import { useQuestionnaireSectionForQuestion } from "../QuestionnaireSectionContext";

import { ContextAwareQuestionWrapper } from "./ContextAwareQuestionWrapper";

const dateInputRegex = /\d{2}\/\d{2}\/\d{4}/;
const displayFormat = "DD/MM/YYYY";
const outputFormat = "YYYY-MM-DD";
// Value returned by Moment.js in case of invalid date
export const invalidDateAnswer = "Invalid date";

export const DateQuestion: React.FC<Omit<QuestionProps, keyof QuestionInjectedProps>> = (props) => {
    const { showError: questionnaireSectionShowError, answer, updateAnswer } = useQuestionnaireSectionForQuestion(props);
    const [inputTouched, setInputTouched] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const [warningMessage, setWarningMessage] = React.useState<string | undefined>(undefined);
    const date = React.useMemo(() => answer ? moment(answer as string) : null, [answer]);
    const [inputDate, setInputDate] = React.useState<string>(date?.isValid() ? date.format(displayFormat) : "");
    const [isFocused, setIsFocused] = React.useState<boolean>(false);

    const { readonly, onAnswerChange, validators, warning, mandatory, questionConfig } = props;
    const questionDisabled = isQuestionDisabled(questionConfig);
    const useV4ClinicalNotesAndReferralReports = getFeatureToggles()[FeatureToggleNames.UseV4ClinicalNotesAndReferralReports];

    React.useEffect(() => {
        const validateAndUpdate = async () => {
            if (readonly || questionDisabled) {
                return;
            }
            if (inputDate === invalidDateAnswer) {
                setInputDate("");
                return;
            }

            const inputAsMoment = inputDate ? moment(inputDate, displayFormat) : null;
            let isValid = false;
            if (mandatory && !inputDate) {
                setError("This field must be completed");
            }
            else {
                const emptyAndNotMandatory = !mandatory && !inputDate;
                const isValidMomentWithProperFormat = inputAsMoment?.isValid() === true && dateInputRegex.test(inputDate);
                isValid = emptyAndNotMandatory || isValidMomentWithProperFormat;
                setError(isValid ? null : "Please enter a valid date");
            }

            const hasCustomValidators = (validators?.length ?? 0) > 0;
            const hasWarningValidators = (warning?.validators?.length ?? 0) > 0;
            updateAnswer({
                answer: inputAsMoment?.format(outputFormat),
                isValid,
                isValidating: inputAsMoment !== null && isValid && (hasCustomValidators || hasWarningValidators)
            });
            onAnswerChange?.(inputAsMoment?.format(outputFormat));

            if (inputAsMoment && isValid && (hasCustomValidators || hasWarningValidators)) {
                const [errors, warningMessages] = await Promise.all([
                    validate(validators || [], inputAsMoment),
                    validate(warning?.validators || [], inputAsMoment)
                ]);
                setError(errors[0]);
                setWarningMessage(warningMessages[0]);
                updateAnswer({
                    answer: inputAsMoment.format(outputFormat),
                    isValid: errors.length === 0 && (!!inputAsMoment || !mandatory),
                    isValidating: false
                });
            } else {
                setWarningMessage(undefined);
            }
        };

        validateAndUpdate();

    // Validators should not be passed in as a dependency to avoid infinite update loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, ([inputDate, mandatory, onAnswerChange, questionDisabled, readonly, updateAnswer] as any[])
        .concat(useV4ClinicalNotesAndReferralReports ? [] : [validators, warning?.validators]));

    const onChange = React.useCallback((event: React.FormEvent<HTMLInputElement>) => setInputDate(event.currentTarget.value), []);

    const onBlur = React.useCallback(() => {
        setInputTouched(true);
        setIsFocused(false);
    }, []);

    const onFocus = React.useCallback(() => {
        setInputTouched(true);
        setIsFocused(true);
    }, []);

    const renderValue = () => {
        if (props.readonly) {
            const answerMoment = answer && moment(answer as string, outputFormat);
            return <div>{answerMoment && answerMoment.isValid() && answerMoment.format(displayFormat)}</div>;
        }
        return (
            <MaskedInput
                data-testid="masked-input"
                type="tel"
                value={inputDate}
                onChange={onChange}
                onBlur={onBlur}
                onFocus={onFocus}
                keepCharPositions={true}
                placeholder={displayFormat}
                guide={false}
                mask={[/\d/, /\d/, "/", /\d/, /\d/, "/", /\d/, /\d/, /\d/, /\d/]}
            />
        );
    };

    React.useEffect(() => {
        // If the question is focused, then the change of answer was triggered by the user so we can ignore it
        if (!isFocused) {
            setInputDate(date?.isValid() ? date.format(displayFormat) : "");
        }
    }, [date, isFocused]);
    if (isQuestionDisabled(props.questionConfig)) {
        return null;
    }

    const showError = inputTouched || questionnaireSectionShowError;
    return (
        <ContextAwareQuestionWrapper
            inline={props.inline}
            question={props.questionConfig.questionText}
            mandatory={props.mandatory}
            hideDivider={props.hideDivider}
            questionClassName={props.questionClassName}
            error={showError ? error : null}
            readonly={props.readonly}
            removable={props.removable ? {
                ...props.removable,
                questionId: props.questionConfig.id,
                isRemovedByUser: props.isRemovedByUser
            } : undefined}
            outdated={{
                ...props.outdated,
                questionId: props.questionConfig.id
            }}
            warningMessage={warningMessage}
        >
            <Form.Field error={showError && !!error}>
                <div className="ui input">
                    {renderValue()}
                </div>
            </Form.Field>
        </ContextAwareQuestionWrapper>
    );
};
